import { Actions, ActionTypes } from '../actions';
import { Alarm, AlarmFilterSettings, AlarmSetting, MeteringPoint } from '@model';
import { MeteringType } from '@enums';

export interface State {
  loaded: boolean;
  loading: boolean;
  created: boolean;
  creating: boolean;
  removed: boolean;
  removing: boolean;
  bulkRemoved: boolean;
  bulkRemoving: boolean;
  bulkUpdated: boolean;
  bulkUpdating: boolean;
  alarms: Alarm[];
  selected: Alarm;
  filterSettings: AlarmFilterSettings;
  filterLoaded: boolean;
  filterLoading: boolean;
  selectedMeteringPoints: MeteringPoint[];
}

export const initialState: State = {
  loaded: false,
  loading: false,
  created: false,
  creating: false,
  removed: false,
  removing: false,
  bulkRemoved: false,
  bulkRemoving: false,
  bulkUpdated: false,
  bulkUpdating: false,
  alarms: [],
  selected: null,
  filterSettings: null,
  filterLoaded: false,
  filterLoading: false,
  selectedMeteringPoints: []
};

export function reducer(state = initialState, action: Actions): State {
  switch (action.type) {
    case ActionTypes.LoadData:
      return { ...state, loaded: false, loading: true };

    case ActionTypes.LoadDataSuccess:
      return { ...state, loaded: true, loading: false, alarms: action.payload };

    case ActionTypes.LoadDataFail:
      return { ...state, loaded: false, loading: false };

    case ActionTypes.Select:
      return { ...state, selected: action.payload };

    case ActionTypes.CreateItem:
      return { ...state, created: false, creating: true };
    case ActionTypes.CreateItemSuccess:
      return { ...state, created: true, creating: false, alarms: [...state.alarms, action.payload] };

    case ActionTypes.BulkCreateItems:
      return { ...state, creating: true, created: false };

    case ActionTypes.BulkCreateItemsSuccess:
      const addedData = action.payload.filter(x => state.alarms.every(y => y.id !== x.id));
      const updatedData = action.payload.filter(x => state.alarms.some(y => y.id === x.id));
      return {
        ...state,
        creating: false,
        created: true,
        alarms:
          action.payload === null
            ? []
            : [
                ...state.alarms.map(alarm =>
                  updatedData.some(u => u.id === alarm.id)
                    ? {
                        ...alarm,
                        alarmSettings: updatedData.filter(u => u.id === alarm.id)[0].alarmSettings
                      }
                    : alarm
                ),
                ...addedData
              ]
      };
    case ActionTypes.BulkCreateItemsFail:
      return {
        ...state,
        creating: false,
        created: false,
        alarms: []
      };

    case ActionTypes.CreateItemAndRedirect:
      return { ...state, created: false, creating: true };
    case ActionTypes.CreateItemAndRedirectSuccess:
      return { ...state, created: true, creating: false, alarms: [...state.alarms, action.payload] };

    case ActionTypes.UpdateItemSuccess:
      const newItem = action.payload;
      return { ...state, selected: null, alarms: state.alarms.map(item => (item.id === newItem.id ? newItem : item)) };

    case ActionTypes.RemoveItem:
      return { ...state, removed: false, removing: true };
    case ActionTypes.RemoveItemSuccess:
      const oldItem = action.payload;
      return {
        ...state,
        removed: true,
        removing: false,
        alarms: state.alarms.filter(item => item.id !== oldItem.id)
      };
    case ActionTypes.RemoveItemFail:
      return {
        ...state,
        removed: false,
        removing: false
      };
    case ActionTypes.BulkRemoveItems:
      return { ...state, bulkRemoved: false, bulkRemoving: true };
  
    case ActionTypes.BulkRemoveItemsSuccess:
      const oldItems = action.payload;
      return {
        ...state,
        bulkRemoved: true,
        bulkRemoving: false,
        alarms: state.alarms.filter(item => !oldItems.some(x => x === item.id))
      };
    case ActionTypes.BulkRemoveItemsFail:
      return { ...state, bulkRemoved: false, bulkRemoving: false };

    case ActionTypes.BulkInactivateItems:
      return { ...state, bulkUpdated: false, bulkUpdating: true };
    case ActionTypes.BulkInactivateItemsSuccess:
      const inactivatedItems = action.payload.filter(x => state.alarms.some(y => y.id === x.id));
      return {
        ...state,
        bulkUpdated: true,
        bulkUpdating: false,
        alarms:
          action.payload === null
          ? []
          : [
              ...state.alarms.map(alarm =>
                inactivatedItems.some(u => u.id === alarm.id)
                ? {
                    ...alarm,
                    isActive: false,
                    alarmSettings: inactivatedItems.filter(u => u.id === alarm.id)[0].alarmSettings
                      .map(x => <AlarmSetting>{ ...x, isActive: false })
                  }
                : alarm
            ),
          ]
      };
    case ActionTypes.BulkInactivateItemsFail:
      return { ...state, bulkUpdated: false, bulkUpdating: false };

    case ActionTypes.BulkActivateItems:
      return { ...state, bulkUpdated: false, bulkUpdating: true };
    case ActionTypes.BulkActivateItemsSuccess:
      const activatedItems = action.payload.filter(x => state.alarms.some(y => y.id === x.id));
      return {
        ...state,
        bulkUpdated: true,
        bulkUpdating: false,
        alarms:
          action.payload === null
          ? []
          : [
              ...state.alarms.map(alarm =>
                activatedItems.some(u => u.id === alarm.id)
                ? {
                    ...alarm,
                    isActive: true,
                    alarmSettings: activatedItems.filter(u => u.id === alarm.id)[0].alarmSettings
                        .map(x => <AlarmSetting>{ ...x, isActive: true })
                  }
                : alarm
            ),
          ]
      };
    case ActionTypes.BulkActivateItemsFail:
      return { ...state, bulkUpdated: false, bulkUpdating: false };


    case ActionTypes.RemoveItemAndRedirect:
      return { ...state, removed: false, removing: true };
    case ActionTypes.RemoveItemAndRedirectSuccess:
      let removedItem = action.payload;
      return {
        ...state,
        removed: true,
        removing: false,
        alarms: state.alarms.filter(item => item.id !== removedItem.id)
      };
    case ActionTypes.RemoveItemAndRedirectFail:
      return {
        ...state,
        removed: false,
        removing: false
      };

    //  Update
    case ActionTypes.UpdateAlarmSettings:
      return { ...state, loading: true, loaded: false };
    case ActionTypes.UpdateAlarmSettingsSuccess:
      let alarmSetting = action.payload;
      let alarmId = alarmSetting[0].alarmId;
      return {
        ...state,
        loaded: true,
        loading: false,
        alarms: state.alarms.map(alarm =>
          alarm.id === alarmId
            ? {
                ...alarm,
                alarmSettings: alarmSetting
              }
            : alarm
        )
      };
    case ActionTypes.UpdateAlarmSettingsFail:
      return { ...state, alarms: [], loading: false, loaded: false };

    // Update and redirect
    case ActionTypes.UpdateAlarmSettingsAndRedirectToAlarms:
      return { ...state, loading: true, loaded: false };
    case ActionTypes.UpdateAlarmSettingsAndRedirectToAlarmsSuccess:
      let aSetting = action.payload;
      let aId = aSetting[0].alarmId;
      return {
        ...state,
        loaded: true,
        loading: false,
        alarms: state.alarms.map(alarm =>
          alarm.id === aId
            ? {
                ...alarm,
                alarmSettings: aSetting
              }
            : alarm
        )
      };
    case ActionTypes.UpdateAlarmSettingsAndRedirectToAlarmsFail:
      return { ...state, alarms: [], loading: false, loaded: false };

    case ActionTypes.UpdateAlarmSettingsAndRedirect:
      return { ...state, loading: true, loaded: false };
    case ActionTypes.UpdateAlarmSettingsAndRedirectSuccess:
      const rAlarmSetting = action.payload;
      const rAlarmId = rAlarmSetting[0].alarmId;
      return {
        ...state,
        loaded: true,
        loading: false,
        alarms: state.alarms.map(alarm =>
          alarm.id === rAlarmId
            ? {
                ...alarm,
                alarmSettings: rAlarmSetting
              }
            : alarm
        )
      };
    case ActionTypes.UpdateAlarmSettingsAndRedirectFail:
      return { ...state, alarms: [], loading: false, loaded: false };

    // Bulk Update AlarmSettings NotificationChannel

    case ActionTypes.BulkUpdateNotificationChannel:
      return { ...state, bulkUpdated: false, bulkUpdating: true };
    case ActionTypes.BulkUpdateNotificationChannelSuccess:
      const updatedItems = action.payload;
      return {
        ...state,
        bulkUpdated: true,
        bulkUpdating: false,
        alarms:
          action.payload === null
          ? []
          : [
              ...state.alarms.map(alarm =>
                updatedItems.some(u => u.alarmId === alarm.id)
                ? {
                    ...alarm,
                    alarmSettings: updatedItems.filter(u => u.alarmId === alarm.id)
                        .map(x => <AlarmSetting>{ ...x })
                  }
                : alarm
            ),
          ]
      };
    case ActionTypes.BulkUpdateNotificationChannelFail:
      return { ...state, bulkUpdated: false, bulkUpdating: false };

    case ActionTypes.SetSelectedMeteringPoints:
      return {
        ...state,
        selectedMeteringPoints: action.payload === null ? [] : action.payload
      };

    case ActionTypes.ClearData:
      return {
        ...state,
        loaded: false,
        loading: false,
        alarms: [],
        selected: null,
        selectedMeteringPoints: []
      };

    // Alarm Filter  
    case ActionTypes.AlarmFilterUpdate:
      if (action.payload.meteringType === MeteringType.Cold) {
        return {
          ...state,
          filterSettings: {
            ...state.filterSettings,
            coldAlarmFilter: action.payload
          }
        };
      } else if (action.payload.meteringType === MeteringType.Hot) {
        return {
          ...state,
          filterSettings: {
            ...state.filterSettings,
            hotAlarmFilter: action.payload
          }
        };
      }
      return state;

    case ActionTypes.AlarmFilterSettingsLoadData:
        return { ...state, filterLoading: true, filterLoaded: false };
    case ActionTypes.AlarmFilterSettingsLoadSuccess:
        return { ...state, filterSettings: action.payload, filterLoading: false, filterLoaded: true };
    case ActionTypes.AlarmFilterSettingsLoadFail:
        return { ...state, filterSettings: null, filterLoading: false, filterLoaded: false };
  }
  return state;
}

export const getAll = (state: State) => state.alarms;
export const getLoaded = (state: State) => state.loaded;
export const getLoading = (state: State) => state.loading;
export const getSelected = (state: State) => state.selected;
export const getAlarmRemoved = (state: State) => state.removed;
export const getBulkAlarmRemoved = (state: State) => state.bulkRemoved;
export const getBulkAlarmInactivated = (state: State) => state.bulkUpdated;
export const getBulkAlarmActivated = (state: State) => state.bulkUpdated;
export const getSelectedMeteringPoints = (state: State) => state.selectedMeteringPoints;
export const getBulkUpdateNotificationChannel = (state: State) => state.bulkUpdated;

export const getAlarmsFilterSettings = (state: State) => state.filterSettings;